import {
  Activity,
  Pill,
  Wind,
  Droplet,
  HeartPulse,
  Microscope,
  Gauge,
} from "lucide-react";

/* Quick-reference cheat sheets — printable, standalone clinical references
   that live alongside the simulators and flashcards. Each sheet is a mix of
   structured tables and short note lists so it renders well on screen AND
   on paper (via window.print()).
 *
   Content style rules:
   - Ranges cite Australian veterinary nursing reference norms
   - When ambiguous, prefer the range given in Merck Vet Manual / RECOVER 2024
   - Include a "lastReviewed" YYYY-MM string so learners can see freshness
   - Every table has an optional `foot` note when the row values need caveats
 *
   Section types supported by the renderer:
     - "table"  { heading, columns:[…], rows:[[…]], foot? }
     - "notes"  { heading, items:[…] }
     - "callout"{ heading, tone:"warning"|"info"|"success", body }
*/

export const CHEAT_SHEETS = [
  {
    id: "vitals-ranges",
    n: 1,
    title: "Vitals & Vital Ranges",
    blurb: "Normal heart rate, respiratory rate, temperature and blood pressure by species.",
    accent: "#0047AB",
    Icon: Activity,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "Heart rate (resting, bpm)",
        type: "table",
        columns: ["Species", "Normal range", "Notes"],
        rows: [
          ["Dog — small breed",  "80–180",   "Toy breeds sit higher; sinus arrhythmia common"],
          ["Dog — medium/large", "60–140",   "Sinus arrhythmia may be prominent at rest"],
          ["Dog — giant",        "50–100",   "Slower baseline; watch for pathologic bradycardia"],
          ["Puppy < 1 yr",       "120–160",  "Higher throughout the first 6 months"],
          ["Cat — adult",        "140–220",  "Clinic stress-tachy is normal; recheck when settled"],
          ["Kitten",             "220–260",  "Tachycardia expected"],
          ["Rabbit",             "180–250",  "Handle gently — stress can trigger fatal arrhythmias"],
          ["Ferret",             "180–250",  "Similar baseline to a small dog"],
          ["Guinea pig",         "230–380",  "Very high baseline; assess with a paediatric stethoscope"],
        ],
      },
      {
        heading: "Respiratory rate (resting, breaths/min)",
        type: "table",
        columns: ["Species", "Normal range", "Notes"],
        rows: [
          ["Dog",         "15–30",   "Pant rates up to 200 while thermoregulating are physiologic"],
          ["Cat",         "20–30",   "Open-mouth breathing at rest is ALWAYS pathological"],
          ["Rabbit",      "30–60",   "Nose-breather; open-mouth breathing = emergency"],
          ["Ferret",      "33–36",   "Small differences swing quickly with stress"],
          ["Guinea pig",  "40–130",  "Very wide; interpret alongside effort not number"],
        ],
      },
      {
        heading: "Temperature (rectal, °C)",
        type: "table",
        columns: ["Species", "Normal range", "Fever threshold"],
        rows: [
          ["Dog",         "38.3–39.2",  "> 39.5 °C = fever"],
          ["Cat",         "38.0–39.2",  "> 39.5 °C = fever; often stress-elevated in clinic"],
          ["Rabbit",      "38.5–40.0",  "> 40.5 °C concerning"],
          ["Ferret",      "37.8–40.0",  "Highly variable — trend more useful than snapshot"],
          ["Guinea pig",  "37.2–39.5",  "> 40 °C concerning"],
        ],
      },
      {
        heading: "Blood pressure & CRT",
        type: "table",
        columns: ["Parameter", "Normal (dog/cat)", "Concerning"],
        rows: [
          ["Systolic BP (mmHg)",    "110–160",  "< 90 hypotension · > 180 hypertension"],
          ["MAP (mmHg)",            "70–110",   "< 60 during anaesthesia = intervene"],
          ["Capillary refill (s)",  "1–2 s",    "> 3 s suggests poor perfusion / shock"],
          ["Mucous membrane colour","Pink, moist","Pale, cyanotic, brick, jaundiced — investigate"],
        ],
      },
      {
        heading: "Quick clinical notes",
        type: "notes",
        items: [
          "Trends matter more than snapshots — chart at least q4h in the stable inpatient.",
          "Rabbits & guinea pigs hide illness; a 'normal' RR with abdominal effort is already decompensating.",
          "Cats stress-tachy AND stress-tachypnoea in-clinic. Recheck after 5 min in a quiet cage before acting on numbers.",
          "Always assess perfusion (MMs + CRT + pulse quality) alongside vital numbers.",
        ],
      },
    ],
  },
  {
    id: "drug-doses",
    n: 2,
    title: "Common Drug Doses",
    blurb: "Reference doses for analgesia, sedation, emergency drugs and antibiotics. Not a substitute for the drug label.",
    accent: "#0e7490",
    Icon: Pill,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "Opioid analgesia",
        type: "table",
        columns: ["Drug", "Dose (dog)", "Dose (cat)", "Route"],
        rows: [
          ["Methadone",   "0.2–0.5 mg/kg", "0.1–0.3 mg/kg",   "IM, IV, SC"],
          ["Buprenorphine","0.01–0.02 mg/kg","0.01–0.02 mg/kg","IM, IV, OTM"],
          ["Butorphanol", "0.1–0.4 mg/kg", "0.1–0.4 mg/kg",   "IM, IV, SC"],
          ["Fentanyl (bolus)","2–5 µg/kg", "1–3 µg/kg",       "IV only"],
          ["Fentanyl CRI","2–10 µg/kg/hr", "1–5 µg/kg/hr",    "IV CRI"],
          ["Tramadol",    "2–5 mg/kg q8–12h","1–2 mg/kg q12h — poor efficacy","PO"],
        ],
        foot: "Titrate to effect. Reduce in geriatric, hepatic or renal-compromised patients.",
      },
      {
        heading: "NSAIDs (post-op / analgesia)",
        type: "table",
        columns: ["Drug", "Dose (dog)", "Dose (cat)", "Route"],
        rows: [
          ["Meloxicam",   "0.2 mg/kg day 1 → 0.1 mg/kg q24h", "0.1 mg/kg day 1 → 0.05 mg/kg q24h (short course)", "PO, SC, IV"],
          ["Carprofen",   "4 mg/kg q24h OR 2 mg/kg q12h", "NOT recommended long-term", "PO, SC, IV"],
          ["Robenacoxib", "1–2 mg/kg q24h", "1–2 mg/kg q24h × up to 6 days", "PO, SC"],
        ],
        foot: "Confirm hydration and normal renal function before dosing. Never combine two NSAIDs, and wait 5–7 days when switching.",
      },
      {
        heading: "Sedatives",
        type: "table",
        columns: ["Drug", "Dose (dog)", "Dose (cat)", "Route"],
        rows: [
          ["Acepromazine",   "0.01–0.05 mg/kg", "0.02–0.05 mg/kg", "IM, IV, SC"],
          ["Medetomidine",   "0.005–0.02 mg/kg","0.01–0.04 mg/kg", "IM, IV"],
          ["Dexmedetomidine","0.003–0.01 mg/kg","0.005–0.02 mg/kg","IM, IV"],
          ["Midazolam",      "0.1–0.3 mg/kg",   "0.1–0.3 mg/kg",    "IM, IV"],
        ],
        foot: "Alpha-2 agonists cause profound bradycardia — never anticholinergic-block routinely; treat only if MAP drops.",
      },
      {
        heading: "Emergency & reversal",
        type: "table",
        columns: ["Drug", "Dose", "Indication"],
        rows: [
          ["Adrenaline (1:1,000, 1 mg/mL)","0.01 mg/kg IV / IO (low dose)", "CPR — first drug q3–5 min"],
          ["Adrenaline (high dose)",       "0.1 mg/kg IV / IO",              "Prolonged CPR after ≥ 10 min without ROSC"],
          ["Atropine",                     "0.02–0.04 mg/kg IV",              "Vagally mediated bradycardia, asystole in CPR"],
          ["Naloxone",                     "0.01–0.04 mg/kg IV / IM",         "Reverse opioid overdose"],
          ["Atipamezole",                  "5 × the medetomidine µg dose IM", "Reverse (dex)medetomidine sedation"],
          ["Flumazenil",                   "0.01 mg/kg IV",                    "Reverse benzodiazepine"],
          ["50% Dextrose",                 "0.5 mL/kg diluted 1:2 or 1:4 IV",  "Symptomatic hypoglycaemia"],
        ],
      },
      {
        heading: "Antibiotics (empirical starting doses)",
        type: "table",
        columns: ["Drug", "Dose (dog/cat)", "Route", "Frequency"],
        rows: [
          ["Amoxicillin/clav", "12.5–25 mg/kg",  "PO, SC, IV", "q8–12h"],
          ["Cefovecin",        "8 mg/kg",         "SC only",    "single dose lasts 14 days"],
          ["Enrofloxacin",     "5 mg/kg (dog) · 5 mg/kg (cat, max)", "PO, IV", "q24h"],
          ["Metronidazole",    "10–15 mg/kg",      "PO, IV",     "q12h"],
          ["Doxycycline",      "5–10 mg/kg",       "PO",         "q12–24h — follow with water in cats"],
        ],
        foot: "Always match choice + duration to culture where available. Higher fluoroquinolone doses in cats risk retinal toxicity.",
      },
      {
        heading: "Important",
        type: "callout",
        tone: "warning",
        body: "This is a study reference, not a substitute for the product data sheet, the vet's prescription or your practice formulary. Always double-check doses against the patient's up-to-date weight and current bloods.",
      },
    ],
  },
  {
    id: "anaesthesia-quickref",
    n: 3,
    title: "Anaesthesia Quick Reference",
    blurb: "ASA classifications, MAC values, monitoring targets, plane depth and IPPV settings.",
    accent: "#0e7490",
    Icon: Wind,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "ASA physical status",
        type: "table",
        columns: ["ASA", "Description", "Example"],
        rows: [
          ["I",   "Normal healthy patient",                       "Elective desexing candidate"],
          ["II",  "Mild systemic disease, no functional limits",  "Well-controlled hypothyroid dog"],
          ["III", "Severe systemic disease, functional limits",   "Uncontrolled diabetes, stable CHF"],
          ["IV",  "Severe systemic disease, constant threat to life", "Septic pyometra, DKA, active CHF"],
          ["V",   "Moribund — not expected to survive without surgery", "GDV with cardiovascular collapse"],
          ["E",   "Emergency suffix — added to any of the above","e.g. ASA IIIE"],
        ],
      },
      {
        heading: "MAC values (approximate, dog)",
        type: "table",
        columns: ["Agent", "MAC (dog)", "MAC (cat)", "Notes"],
        rows: [
          ["Isoflurane",  "1.28%", "1.63%", "Most common inhalant in Australia"],
          ["Sevoflurane", "2.3%",  "2.6%",  "Faster on/off; less airway-irritant"],
          ["Nitrous oxide","≈188%","≈255%", "Adjunct only — never sole agent"],
        ],
        foot: "MAC is the ED50 (50% of patients don't move to noxious stimulus). Reduce vaporiser 20–30% when using potent opioid co-drugs.",
      },
      {
        heading: "Plane of anaesthesia (Stage III)",
        type: "table",
        columns: ["Plane", "Eye position", "Palpebral", "Jaw tone", "Vital trend"],
        rows: [
          ["1 (light)",    "Central, brisk lacrimation", "Present",         "Firm",    "May move with stimulation"],
          ["2 (surgical)", "Ventromedial",               "Slow / absent",   "Slight",  "Stable HR / RR, normal BP"],
          ["3 (deep)",     "Central again",              "Absent",          "Flaccid", "BP falling, HR drops"],
          ["4 (too deep)", "Central, dry",               "Absent",          "None",    "Impending respiratory arrest — LIGHTEN"],
        ],
      },
      {
        heading: "Monitoring targets (intra-op)",
        type: "table",
        columns: ["Parameter", "Target", "Alert"],
        rows: [
          ["HR (dog)",         "60–140 bpm",   "< 50 or > 160 → investigate"],
          ["HR (cat)",         "120–200 bpm",  "< 100 or > 220 → investigate"],
          ["Systolic BP",      "> 90 mmHg",    "< 90 → hypotensive: check depth, fluid, dobutamine"],
          ["MAP",              "> 60 mmHg",    "Same escalation as SBP"],
          ["SpO₂",             "≥ 95%",        "< 92% → check tube position, O2 flow, patient colour"],
          ["EtCO₂",            "35–45 mmHg",   "> 55 hypoventilating → assist / IPPV. < 25 → deep, hyperventilating"],
          ["Temperature (°C)", "36.5–38.5",    "< 36 → active warming; < 34 = severe hypothermia"],
        ],
      },
      {
        heading: "IPPV settings (healthy dog)",
        type: "table",
        columns: ["Setting", "Value"],
        rows: [
          ["Tidal volume",         "10–15 mL/kg"],
          ["Peak inspiratory pressure","10–15 cmH₂O (max ~20)"],
          ["Respiratory rate",     "8–12 bpm"],
          ["I:E ratio",            "1 : 2 to 1 : 3"],
          ["Target EtCO₂",         "35–45 mmHg"],
        ],
        foot: "Reduce PIP for cats (~10 cmH₂O), asthmatic patients, and any patient with a pneumothorax risk.",
      },
      {
        heading: "Rules of thumb",
        type: "notes",
        items: [
          "Never fix a drop in SpO₂ by turning UP the vaporiser — check the patient (tube, colour, circuit) first.",
          "Hypothermia is the most common under-diagnosed anaesthetic complication. Start warming BEFORE induction.",
          "Alpha-2 sedation drops HR by design — treat MAP, not HR, if perfusion is intact.",
          "The palpebral reflex is not reliable in cats — use jaw tone + eye position + vitals instead.",
        ],
      },
    ],
  },
  {
    id: "fluid-therapy",
    n: 4,
    title: "Fluid Therapy",
    blurb: "Maintenance rates, shock boluses, deficit calculations and blood transfusion volumes.",
    accent: "#0f766e",
    Icon: Droplet,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "Maintenance rates",
        type: "table",
        columns: ["Species", "Rate (mL/kg/hr)", "Rate (mL/kg/day)"],
        rows: [
          ["Dog — adult",  "2",   "40–60"],
          ["Cat — adult",  "1.5", "30–40"],
          ["Puppy",        "3–4", "80–120"],
          ["Kitten",       "3",   "80–100"],
          ["Rabbit",       "3",   "100"],
        ],
        foot: "Halve maintenance rate in patients with cardiac disease or renal impairment. ALWAYS adjust for ongoing losses.",
      },
      {
        heading: "Shock rate crystalloid bolus",
        type: "table",
        columns: ["Species", "Total dose", "Per aliquot", "Reassess"],
        rows: [
          ["Dog", "60–90 mL/kg over 15–20 min", "15–20 mL/kg", "After each 1/4 dose (HR, MMs, CRT, BP)"],
          ["Cat", "40–60 mL/kg (max)",           "10–15 mL/kg", "After each 1/4 dose — cats fluid-overload fast"],
        ],
        foot: "Reassess perfusion (MMs, CRT, HR, mentation, BP) between each aliquot. Stop at the smallest volume that restores perfusion.",
      },
      {
        heading: "Dehydration deficit calculation",
        type: "table",
        columns: ["Clinical sign", "% dehydration"],
        rows: [
          ["Normal MMs, brisk CRT, no skin tent", "< 5%"],
          ["Slightly tacky MMs, mild skin tent",  "5–6%"],
          ["Dry MMs, moderate skin tent, slow CRT","7–8%"],
          ["Very dry, sunken eyes, poor pulses",   "10–12%"],
          ["Moribund, collapsed",                   "> 12%"],
        ],
        foot: "Deficit (mL) = BW(kg) × % dehydration × 10. Replace over 12–24 h alongside ongoing maintenance + losses.",
      },
      {
        heading: "Blood product volumes",
        type: "table",
        columns: ["Product", "Dose", "Rate", "Notes"],
        rows: [
          ["Packed RBCs",        "10–15 mL/kg",  "Over 4 h (max)", "Start at 0.25 mL/kg/hr for 15 min, watch for reaction"],
          ["Whole blood",        "20 mL/kg",     "Over 4 h",       "1 mL/kg raises PCV by ~1%"],
          ["Fresh frozen plasma","6–10 mL/kg",   "Over 1–3 h",     "For coagulopathy — thaw at ≤ 37 °C"],
          ["Cryoprecipitate",    "1 unit / 10 kg","Rapid bolus",    "For vWF deficiency, haemophilia A"],
        ],
      },
      {
        heading: "Fluid choice quick guide",
        type: "notes",
        items: [
          "Balanced crystalloid (Hartmann's/Plasmalyte) — first choice for most surgical, dehydration and shock scenarios.",
          "0.9% NaCl — good for hyponatraemia, hypercalcaemia, oliguric renal failure. Avoid in acidosis (its own pH is 5.5).",
          "5% dextrose in water — free water, use for pure water deficit / hypernatraemia. Never as sole shock fluid.",
          "Hypertonic saline (7.5%) — 4 mL/kg IV over 10 min — head trauma, GDV with hypotension. Follow with crystalloid.",
        ],
      },
    ],
  },
  {
    id: "emergency-cpr",
    n: 5,
    title: "Emergency & CPR",
    blurb: "RECOVER-based CPR sequence, defibrillation, adrenaline dose and post-arrest care.",
    accent: "#b45309",
    Icon: HeartPulse,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "Recognise arrest → immediate action",
        type: "notes",
        items: [
          "Unresponsive + apnoea (or agonal breathing) + no palpable pulse for 10 seconds = CPR. Don't wait for asystole confirmation.",
          "Call it aloud — assign a leader, a compressor, a ventilator, a drug-nurse and a recorder BEFORE starting.",
          "Start compressions within 15 seconds of recognition. Time-to-CPR is the single best predictor of ROSC.",
        ],
      },
      {
        heading: "Basic Life Support (BLS)",
        type: "table",
        columns: ["Task", "Target"],
        rows: [
          ["Chest compression rate",  "100–120 per minute"],
          ["Compression depth",       "1/3 to 1/2 chest width"],
          ["Compression cycle",       "2 min uninterrupted, then swap compressor"],
          ["Ventilation (intubated)", "10 breaths/min, tidal volume 10 mL/kg"],
          ["Ventilation (mouth-to-snout)","30 : 2 ratio (compressions : breaths)"],
        ],
        foot: "Chest lateral for round-chested dogs and cats. Sternal (dorsal recumbency) for barrel-chested or wide dogs.",
      },
      {
        heading: "Advanced Life Support (ALS) drugs",
        type: "table",
        columns: ["Drug", "Dose", "When"],
        rows: [
          ["Adrenaline (low)",  "0.01 mg/kg IV / IO", "First drug — repeat every 3–5 min"],
          ["Adrenaline (high)", "0.1 mg/kg IV / IO",  "After 10 min of CPR without ROSC"],
          ["Vasopressin",       "0.8 U/kg IV / IO",    "Alternate with adrenaline (optional)"],
          ["Atropine",          "0.04 mg/kg IV / IO",  "Vagal-mediated arrest, asystole"],
          ["Naloxone",          "0.04 mg/kg IV / IM",  "Suspected opioid overdose"],
          ["50% dextrose",      "0.5 mL/kg IV diluted","If BG confirmed low"],
        ],
      },
      {
        heading: "Defibrillation",
        type: "table",
        columns: ["Rhythm", "Action"],
        rows: [
          ["Pulseless VT / VF",            "Defibrillate: monophasic 4–6 J/kg · biphasic 2–4 J/kg"],
          ["Asystole",                     "CPR + adrenaline. DO NOT defib — the heart is silent, not fibrillating"],
          ["PEA (pulseless electrical activity)", "CPR + adrenaline. Search for reversible cause (see below)"],
        ],
      },
      {
        heading: "Reversible causes — the 4 Hs and 4 Ts",
        type: "notes",
        items: [
          "Hypovolaemia · Hypoxia · Hyper/hypokalaemia · Hypothermia",
          "Tension pneumothorax · Tamponade (cardiac) · Toxins · Thrombosis (pulmonary or coronary)",
          "Address these DURING CPR — don't wait for ROSC to check.",
        ],
      },
      {
        heading: "Post-arrest priorities (after ROSC)",
        type: "notes",
        items: [
          "Ventilation — target EtCO₂ 35–45 mmHg. Avoid hyperventilation.",
          "Oxygenation — target SpO₂ 94–98%. Avoid 100% for prolonged periods (reperfusion injury).",
          "Perfusion — target MAP > 80 mmHg with fluids ± noradrenaline / dobutamine.",
          "Neuro — mild targeted temperature management (32–34 °C) considered for severe post-arrest cases.",
          "Discuss with the client immediately — expectations should be tempered even after ROSC.",
        ],
      },
    ],
  },
  {
    id: "clinpath-ranges",
    n: 6,
    title: "Clinical Pathology Ranges",
    blurb: "Reference ranges for haematology, biochemistry and urinalysis — dog and cat.",
    accent: "#7c3aed",
    Icon: Microscope,
    lastReviewed: "2026-02",
    sections: [
      {
        heading: "Haematology (CBC)",
        type: "table",
        columns: ["Parameter", "Dog", "Cat"],
        rows: [
          ["PCV (%)",              "37–55",     "30–45"],
          ["Haemoglobin (g/L)",    "120–180",   "100–150"],
          ["RBC (×10¹²/L)",         "5.5–8.5",   "5.0–10.0"],
          ["MCV (fL)",             "60–77",     "39–55"],
          ["Platelets (×10⁹/L)",   "200–500",   "300–800"],
          ["WBC (×10⁹/L)",         "6.0–17.0",  "5.5–19.5"],
          ["Neutrophils (×10⁹/L)", "3.0–11.5",  "2.5–12.5"],
          ["Lymphocytes (×10⁹/L)", "1.0–4.8",   "1.5–7.0"],
        ],
        foot: "Cats routinely stress-neutrophilia and stress-lymphopenia. Always interpret with clinical picture.",
      },
      {
        heading: "Biochemistry",
        type: "table",
        columns: ["Parameter", "Dog", "Cat"],
        rows: [
          ["Urea (mmol/L)",       "3.0–9.0",     "6.0–13.0"],
          ["Creatinine (µmol/L)", "40–130",      "70–160"],
          ["ALT (U/L)",           "10–100",      "10–100"],
          ["ALP (U/L)",           "20–150",      "0–90 (breed-dependent)"],
          ["Total protein (g/L)", "55–75",       "60–80"],
          ["Albumin (g/L)",       "25–40",       "24–40"],
          ["Glucose (mmol/L)",    "3.9–6.7",     "3.9–8.4"],
          ["Calcium (mmol/L)",    "2.3–3.0",     "2.2–2.7"],
          ["Phosphate (mmol/L)",  "0.9–1.9",     "1.0–2.4"],
          ["Sodium (mmol/L)",     "140–155",     "150–160"],
          ["Potassium (mmol/L)",  "3.5–5.5",     "3.6–5.2"],
          ["Chloride (mmol/L)",   "105–120",     "110–125"],
        ],
        foot: "Reference intervals vary by lab and assay. Use YOUR analyser's package insert for definitive interpretation.",
      },
      {
        heading: "Urinalysis",
        type: "table",
        columns: ["Parameter", "Dog", "Cat"],
        rows: [
          ["USG",                       "1.015–1.045",  "1.035–1.060"],
          ["Isosthenuria band",         "1.008–1.012",  "1.008–1.012"],
          ["pH",                        "5.5–7.5",       "5.5–7.5"],
          ["Protein",                   "Negative to trace","Negative to trace"],
          ["Glucose",                   "Negative",       "Negative"],
          ["Ketones",                   "Negative",       "Negative"],
          ["Blood",                     "Negative",       "Negative"],
          ["Sediment: RBC/hpf",         "0–5",             "0–5"],
          ["Sediment: WBC/hpf",         "0–5",             "0–5"],
          ["Sediment: bacteria",        "Absent (fresh sample)","Absent"],
        ],
        foot: "Free-catch samples are unreliable for sediment. Cystocentesis is the gold standard for culture.",
      },
      {
        heading: "Blood gas (venous, dog/cat)",
        type: "table",
        columns: ["Parameter", "Range"],
        rows: [
          ["pH",         "7.32–7.42"],
          ["PvCO₂",      "35–45 mmHg"],
          ["HCO₃⁻",      "20–25 mmol/L"],
          ["Base excess","−4 to +4 mmol/L"],
          ["Lactate",    "< 2.5 mmol/L"],
        ],
        foot: "Arterial values differ (pH slightly higher, PaCO₂ slightly lower). Always tag the sample with the source.",
      },
      {
        heading: "Interpretive tips",
        type: "notes",
        items: [
          "Trend > snapshot — a rising creatinine over 3 days matters more than one 'high' result.",
          "Lipaemia + haemolysis + icterus all interfere with dry-chemistry biochemistry. Flag the sample when you spin it.",
          "USG 1.008–1.012 (isosthenuric) in an azotaemic patient suggests renal-origin azotaemia, not pre-renal.",
          "Cats concentrate urine harder than dogs — a dog with USG 1.030 is normal; a cat with USG 1.030 may be under-concentrating.",
        ],
      },
    ],
  },
  {
    id: "admission-a3",
    n: 7,
    title: "Pre-Admission Checklist · A3",
    blurb: "Full pre-anaesthetic admission workflow — critical client questions, breed-specific concerns, medications to flag, exam checklist and hand-over to the anaesthesia bay. Designed for A3 landscape.",
    accent: "#0047AB",
    Icon: Gauge,
    lastReviewed: "2026-02",
    layout: "a3",
    orientation: "landscape",
    sections: [
      /* Column 1 — Client interview + history */
      {
        heading: "Critical client questions — every case",
        type: "notes",
        items: [
          "Fasting: last meal AND last water — cross-check with any diabetic protocol.",
          "Have you noticed vomiting, diarrhoea, coughing, sneezing or exercise intolerance in the last 7 days?",
          "Any breathing changes at rest, snoring louder than usual, or heat intolerance? (Brachy alert)",
          "Any known reactions to sedation, anaesthesia or drugs at any previous vet visit?",
          "When did they last urinate & pass faeces? Any straining or blood?",
          "Any change in appetite, thirst or weight in the last month?",
          "Currently on any medications, herbs or supplements — including flea/tick, joint, calming?",
          "Bitches: could she be in season, pregnant or recently mated?",
          "Any home stress overnight (thunderstorm, new pet, boarding) that could affect fasting compliance?",
          "Client contact: mobile number confirmed & reachable for the next 4 hours?",
        ],
      },
      {
        heading: "Dog-specific admission questions",
        type: "notes",
        items: [
          "Any collapse, syncope, weakness or reluctance to exercise? (Cardiac / MDR1 screen)",
          "Bleeding: gum bleeds when chewing? Bruising? Prolonged bleeding from a nail trim?",
          "Snoring, gagging, retching or reverse-sneeze episodes? (Brachy airway)",
          "Bloat / GDV history in dog or first-degree relative? (Deep-chested breeds)",
          "Any diabetes, Cushings, Addisons, hypothyroidism — currently on treatment?",
          "Recent flea, tick or worm treatment — product AND date? (ivermectin exposure = MDR1)",
        ],
      },
      {
        heading: "Cat-specific admission questions",
        type: "notes",
        items: [
          "Any labour to breathe, open-mouth breathing or heavy purring at rest? (Occult HCM)",
          "Vomiting frequency — hairball vs. bile vs. undigested food?",
          "FIV / FeLV status known? Last test date?",
          "Indoor / outdoor — potential toxin, lily or antifreeze exposure?",
          "Any straining in the litter tray, blood or gritty urine? (FLUTD / obstruction)",
          "Any known heart murmur ever mentioned by a vet?",
        ],
      },
      {
        heading: "Consent, fasting & pre-op admin",
        type: "notes",
        items: [
          "Signed consent covers: procedure · anaesthesia · additional procedures at surgeon discretion.",
          "Cost estimate signed & upper limit noted on file.",
          "Contact number confirmed AND alternate contact if primary unreachable.",
          "Vaccination status current OR client acknowledges the risk.",
          "Fasting confirmed: dogs/cats 6–8 hr food, water free until 1 hr pre-op. Rabbits/GPs DO NOT fast.",
          "Weigh in kg — 1 decimal — recorded on the anaesthetic sheet.",
        ],
      },

      /* Column 2 — Breed-specific & medications */
      {
        heading: "Breed alerts — Dogs",
        type: "table",
        columns: ["Breed group", "Watch for"],
        rows: [
          ["Brachycephalic (Bulldog, Pug, Frenchie, Boxer)", "Stertor, small trachea, elongated palate → PRE-OXYGENATE, avoid ace, extubate LATE"],
          ["Sighthounds (Greyhound, Whippet)", "Low body fat = thiopentone lasts longer; hyperthermia post-op; prolonged prop recovery"],
          ["Doberman / German Shepherd", "von Willebrand's — check BMBT or vWF assay pre-elective; also DCM risk"],
          ["Boxer", "Vagally mediated bradycardia with ace; occult DCM"],
          ["MDR1 breeds (Collie, Aussie Shep, Sheltie)", "Ivermectin, loperamide, macrocyclic lactones → severe CNS effects; test if unsure"],
          ["Deep-chested (Great Dane, Setter, Std Poodle)", "GDV risk — do NOT fast > 12 hr; recover in sternal"],
          ["Toy breeds (Chihuahua, Yorkie, Pom)", "Hypoglycaemia, hypothermia, tracheal collapse; small ETT sizes"],
          ["Retrievers (Lab, Golden)", "Obesity → dose to LEAN weight; laryngeal paralysis in seniors"],
        ],
      },
      {
        heading: "Breed alerts — Cats",
        type: "table",
        columns: ["Breed", "Watch for"],
        rows: [
          ["Persian / Himalayan / Exotic SH", "Brachy airway, PKD — check USG + creatinine"],
          ["Maine Coon", "HCM prevalence ~30% — auscultate carefully; consider pro-BNP"],
          ["Ragdoll", "HCM common; sometimes prolonged ketamine recovery"],
          ["Sphynx", "Cardiac disease common; hypothermia risk — warm early"],
          ["Devon Rex / Cornish Rex", "Malignant-hyperthermia-like reactions reported (rare); watch temp"],
          ["Bengal", "Chronic diarrhoea common; may be underweight — check BCS"],
          ["Any senior cat", "Assume CKD until proven otherwise — check USG + IRIS"],
        ],
      },
      {
        heading: "Medications to flag on admission",
        type: "table",
        columns: ["Class / drug", "Concern", "Action"],
        rows: [
          ["NSAIDs (meloxicam, carprofen)", "Renal / GI risk with hypotension", "Don't dose again pre-op; check bloods"],
          ["ACE inhibitors (benazepril, enalapril)", "Refractory hypotension under GA", "Hold morning dose"],
          ["Diuretics (furosemide)", "Volume depletion + K⁺ loss", "Consider holding; check K⁺"],
          ["Insulin", "Hypoglycaemia during fasting", "½ dose morning; BG on arrival"],
          ["Corticosteroids (prednisolone)", "Adrenal suppression + delayed healing", "Don't stop abruptly; note on chart"],
          ["Gabapentin / trazodone (calmer)", "Additive sedation, hypotension", "Reduce pre-med 25–50%"],
          ["Thyroxine", "Continue as normal", "Give with tiny food morning of surgery"],
          ["Warfarin / clopidogrel", "Bleeding risk", "MUST stop 5–7 days elective; call surgeon"],
          ["Selegiline (MAOI)", "Interaction with tramadol, pethidine", "Avoid serotonergic opioids"],
          ["Anti-arrhythmics (diltiazem, sotalol)", "Bradycardia potentiation", "Continue but watch HR"],
          ["St John's Wort / calming supplements", "Serotonin syndrome potential", "Note on chart; avoid tramadol"],
        ],
      },

      /* Column 3 — Physical exam, IV, pre-med, handover */
      {
        heading: "Physical exam — pre-anaesthesia",
        type: "notes",
        items: [
          "TPR: temperature (rectal), pulse (fem/dig), respiration count — record with the time.",
          "MMs & CRT: pink, moist, ≤ 2 s? Pale, injected, tacky or icteric = stop-and-call moment.",
          "Auscultate BOTH sides of thorax for at least 30 s — murmurs, arrhythmia, dull lung sounds.",
          "Palpate abdomen: masses, discomfort, distended bladder (esp. blocked cats).",
          "Peripheral pulse quality: femoral AND dorsal pedal — asymmetry may indicate saddle thrombus.",
          "Body condition score (BCS 1–9) + hydration estimate — dose to LEAN weight.",
          "Weight in kg (single decimal) — reconfirm on the anaesthetic sheet.",
          "Any lameness, spinal pain or reluctance to be handled — note the side.",
        ],
      },
      {
        heading: "IV catheter placement",
        type: "notes",
        items: [
          "Clip generously — ≥ 5 cm proximal to distal to insertion site.",
          "Aseptic prep: chlorhex ≥ 30 s contact, then alcohol, repeated ×3.",
          "Size: 22G cats/toys · 20G small-med dog · 18G medium-large · 16G if bolus / blood.",
          "Confirm patency with saline flush BEFORE taping — no swelling or resistance.",
          "Secure with T-port + tape + light bandage — label date, time, size, initials.",
          "Recheck 15 min later — perivascular fluid is a preventable disaster.",
        ],
      },
      {
        heading: "Pre-medication",
        type: "notes",
        items: [
          "Calculate to LEAN weight (obese dogs) — over-dose = prolonged recovery.",
          "Record: drug, mg (not mL), mL drawn, route, time — before administering.",
          "IM standard; IV in shock or already catheterised.",
          "Wait FULL 15–20 min for effect before induction — most under-sedation is impatience.",
          "Monitor: HR, RR, sedation score at 5, 10, 15 min.",
          "Never leave sedated patient unattended in a run — falls, aspiration, hypothermia.",
        ],
      },
      {
        heading: "Hand-over to anaesthesia bay",
        type: "notes",
        items: [
          "Patient identified: name, chip #, colour, breed, sex — cross-checked verbally with surgical nurse.",
          "Procedure and side confirmed against consent form — mark the site if unilateral.",
          "Anaesthetic sheet: weight, ASA, drugs given, times, allergies, in-patient meds, phone.",
          "IV catheter checked and flushed at hand-over.",
          "Warming already in place — hypothermia starts BEFORE induction.",
          "Records signed on both sides: admission nurse + anaesthesia nurse.",
        ],
      },
      {
        heading: "Red flags — DO NOT proceed",
        type: "callout",
        tone: "warning",
        body: "Rectal temp < 37 or > 39.5 °C · new murmur ≥ III/VI · pale/tacky MMs · vomiting inside the fasting window · bradycardia < 60 in small dog or < 100 in cat · dyspnoea at rest · client withdrew consent · patient escaped a run or fell — stop and call the vet.",
      },
    ],
  },
  {
    id: "anaesthesia-a3",
    n: 8,
    // Hidden 2026-07-21 — replaced by `anaesthesia-a3-tables` (table-form
    // volumes instead of dose-vs-weight charts). Kept in source so it can
    // be restored later by removing the `hidden` flag.
    hidden: true,
    title: "Anaesthesia Crib Sheet · A3",
    blurb: "ET tube sizes, bag sizes, O₂ flows, iso/sevo dial, pre-meds, CRIs, syringe-volume-vs-weight charts, monitoring targets, hypotension ladder, CPR — the anaesthetic table reference.",
    accent: "#0f766e",
    Icon: Gauge,
    lastReviewed: "2026-02",
    layout: "a3",
    orientation: "landscape",
    sections: [
      /* Column 1 — Airway & Circuits */
      {
        heading: "Endotracheal tube (ETT) sizing",
        type: "table",
        columns: ["Body weight", "Dog · ID (mm)", "Cat · ID (mm)"],
        rows: [
          ["< 2 kg",       "3.0–4.0", "3.0–3.5"],
          ["2–5 kg",       "4.0–5.0", "3.5–4.0"],
          ["5–10 kg",      "5.0–7.0", "4.0–4.5"],
          ["10–20 kg",     "7.0–9.0", "—"],
          ["20–30 kg",     "8.0–10.0", "—"],
          ["30–45 kg",     "9.0–11.0", "—"],
          ["> 45 kg",      "11.0–14.0", "—"],
        ],
        foot: "Brachy: downsize 0.5–1.0 mm. Cats: uncuffed or low-pressure cuff only.",
      },
      {
        heading: "Reservoir (rebreathing) bag",
        type: "table",
        columns: ["Patient weight", "Bag size"],
        rows: [
          ["< 2 kg",    "0.5 L"],
          ["2–7 kg",    "1 L"],
          ["7–15 kg",   "2 L"],
          ["15–40 kg",  "3 L"],
          ["> 40 kg",   "5 L"],
        ],
        foot: "Rule: bag ≥ 3× tidal volume (≈ 30 mL/kg minimum).",
      },
      {
        heading: "Circuit choice & fresh gas flow",
        type: "table",
        columns: ["Circuit / weight", "Induction flow", "Maintenance flow"],
        rows: [
          ["T-piece · < 5 kg",              "300 mL/kg/min",  "Same"],
          ["Bain · 5–10 kg",                "200–300 mL/kg/min", "Same"],
          ["Paed. circle · 10–20 kg",        "30–50 mL/kg/min",  "20–30 mL/kg/min"],
          ["Adult circle · > 20 kg",         "30–50 mL/kg/min",  "20–30 mL/kg/min"],
          ["Low-flow (any circle)",         "—",                "15–20 mL/kg/min"],
        ],
        foot: "Non-rebreathing = no CO₂ absorber → keep flow high. Rebreathing can drop after 10–15 min.",
      },
      {
        heading: "Isoflurane vaporiser dial",
        type: "table",
        columns: ["Phase", "Dog", "Cat"],
        rows: [
          ["Mask / chamber induction",  "3–5% (rare)", "3–4% chamber"],
          ["Maintenance surgical",       "1.5–2.0%",    "1.5–2.0%"],
          ["Short deepening burst",      "up to 2.5%",  "up to 2.5%"],
          ["Recovery (off + O₂)",         "O₂ 5–10 min", "O₂ 5–10 min"],
        ],
        foot: "MAC dog 1.28% · cat 1.63%. Potent opioid CRI → cut dial 20–30%.",
      },
      {
        heading: "Sevoflurane vaporiser dial",
        type: "table",
        columns: ["Phase", "Dog", "Cat"],
        rows: [
          ["Mask / chamber",  "5–7%",     "5–6%"],
          ["Maintenance",     "2.5–3.5%", "2.5–3.5%"],
          ["Recovery",        "O₂ 5 min", "O₂ 5 min"],
        ],
        foot: "MAC dog 2.3% · cat 2.6%.",
      },
      {
        heading: "Local anaesthetic MAX dose",
        type: "table",
        columns: ["Agent", "Dog", "Cat", "Onset · Duration"],
        rows: [
          ["Lidocaine 2%",     "10 mg/kg", "4 mg/kg", "3 min · 60–90 min"],
          ["Bupivacaine 0.5%", "2 mg/kg",  "1 mg/kg", "15 min · 4–6 hr"],
          ["Ropivacaine 0.75%","2 mg/kg",  "1 mg/kg", "10 min · 4–6 hr"],
          ["Mepivacaine 2%",   "5 mg/kg",  "3 mg/kg", "5 min · 90 min–3 hr"],
        ],
        foot: "Aspirate before injection. Combined blocks: sum toward the same limit.",
      },

      /* Column 2 — Dose charts + protocols */
      {
        heading: "Propofol · volume vs. weight (IV induction)",
        type: "chart",
        xLabel: "Weight (kg)",
        yLabel: "Volume to draw (mL)",
        xMax: 40,
        yMax: 24,
        yTicks: [0, 6, 12, 18, 24],
        xTicks: [0, 10, 20, 30, 40],
        series: [
          { label: "Dog & Cat · 4–6 mg/kg (10 mg/mL vial)", color: "#0f766e", min: 0.4, max: 0.6 },
        ],
        foot: "Vial 10 mg/mL. Worked example: 20 kg dog at 4 mg/kg = 80 mg ÷ 10 = 8 mL. Titrate ¼ at a time.",
      },
      {
        heading: "Alfaxalone · volume vs. weight (IV induction)",
        type: "chart",
        xLabel: "Weight (kg)",
        yLabel: "Volume to draw (mL)",
        xMax: 40,
        yMax: 20,
        yTicks: [0, 5, 10, 15, 20],
        xTicks: [0, 10, 20, 30, 40],
        series: [
          { label: "Dog · 2–3 mg/kg (10 mg/mL vial)",  color: "#0f766e", min: 0.2, max: 0.3 },
          { label: "Cat · 2–5 mg/kg (10 mg/mL vial)",  color: "#dc2626", min: 0.2, max: 0.5 },
        ],
        foot: "Vial 10 mg/mL. Worked example: 5 kg cat at 3 mg/kg = 15 mg ÷ 10 = 1.5 mL. Give slowly to avoid transient apnoea.",
      },
      {
        heading: "Methadone · volume vs. weight (IM pre-med)",
        type: "chart",
        xLabel: "Weight (kg)",
        yLabel: "Volume to draw (mL)",
        xMax: 40,
        yMax: 1.5,
        yTicks: [0, 0.3, 0.6, 0.9, 1.2, 1.5],
        xTicks: [0, 10, 20, 30, 40],
        series: [
          { label: "Dog · 0.2–0.3 mg/kg (10 mg/mL vial)",  color: "#0f766e", min: 0.02, max: 0.03 },
          { label: "Cat · 0.1–0.3 mg/kg (10 mg/mL vial)",  color: "#dc2626", min: 0.01, max: 0.03 },
        ],
        foot: "Vial 10 mg/mL. Worked example: 20 kg dog at 0.3 mg/kg = 6 mg ÷ 10 = 0.6 mL.",
      },
      {
        heading: "Dexmedetomidine · volume vs. weight (IM pre-med)",
        type: "chart",
        xLabel: "Weight (kg)",
        yLabel: "Volume to draw (mL)",
        xMax: 40,
        yMax: 1.6,
        yTicks: [0, 0.4, 0.8, 1.2, 1.6],
        xTicks: [0, 10, 20, 30, 40],
        series: [
          { label: "Dog · 3–10 µg/kg (500 µg/mL vial)",  color: "#0f766e", min: 0.006, max: 0.02 },
          { label: "Cat · 5–20 µg/kg (500 µg/mL vial)",  color: "#dc2626", min: 0.01, max: 0.04 },
        ],
        foot: "Vial 500 µg/mL. Worked example: 20 kg dog at 5 µg/kg = 100 µg ÷ 500 = 0.2 mL. Reverse: atipamezole IM = 5× the dex µg dose.",
      },
      {
        heading: "Pre-med combos · Dog (IM)",
        type: "table",
        columns: ["Patient", "Combination"],
        rows: [
          ["Healthy adult", "Medetomidine 5–10 + Methadone 0.2–0.3"],
          ["Nervous",       "Dex 5 + Butorphanol 0.2"],
          ["Painful",       "Medetomidine 10 + Methadone 0.3"],
          ["Frail",         "Butorphanol 0.2 ± Ace 0.01"],
        ],
        foot: "All doses µg or mg /kg IM, 15–20 min before induction.",
      },
      {
        heading: "Pre-med combos · Cat (IM)",
        type: "table",
        columns: ["Patient", "Combination"],
        rows: [
          ["Healthy adult", "Dex 5 + Buprenorphine 0.02"],
          ["Painful",       "Dex 10 + Methadone 0.3"],
          ["Fractious",     "Dex 10 + Ketamine 5 + Buprenorphine 0.02"],
          ["Frail",         "Butorphanol 0.2 alone"],
        ],
      },
      {
        heading: "Intra-op analgesic CRIs",
        type: "table",
        columns: ["Drug", "Loading", "CRI rate", "Notes"],
        rows: [
          ["Fentanyl",           "2–5 µg/kg IV",  "2–10 µg/kg/hr", "MAC ↓ 20–30%"],
          ["Ketamine",           "0.5 mg/kg IV",  "0.6 mg/kg/hr",  "Wind-up prevention"],
          ["Lidocaine (dog)",    "1–2 mg/kg IV",  "25–50 µg/kg/min","CATS: DO NOT USE"],
          ["MLK (dog)",          "See loading",   "Combined bag",   "Multi-modal std."],
        ],
      },

      /* Column 3 — Monitoring, IPPV, Emergency */
      {
        heading: "Intra-op monitoring targets",
        type: "table",
        columns: ["Parameter", "Dog", "Cat", "Alert"],
        rows: [
          ["HR",         "60–140",     "120–200",    "Out of range → investigate"],
          ["SBP",        "> 90 mmHg",   "> 90 mmHg",   "Hypotensive"],
          ["MAP",        "> 60 mmHg",   "> 60 mmHg",   "Intervene"],
          ["SpO₂",       "≥ 95%",        "≥ 95%",        "< 92% → check circuit + tube"],
          ["EtCO₂",      "35–45",        "35–45",        "> 55 → ventilate · < 25 → deep"],
          ["Temp (°C)",  "36.5–38.5",    "36.5–38.5",    "< 36 → warm; < 34 = severe"],
        ],
      },
      {
        heading: "Plane of anaesthesia (Stage III)",
        type: "table",
        columns: ["Plane", "Eye", "Palpebral", "Jaw", "Vitals"],
        rows: [
          ["Light",     "Central",       "Present",     "Firm",     "May move"],
          ["Surgical",  "Ventromedial",   "Slow / abs",  "Slight",   "Stable"],
          ["Deep",      "Central",       "Absent",      "Flaccid",  "BP falls"],
          ["Too deep",  "Central + dry", "Absent",      "None",     "LIGHTEN — arrest imminent"],
        ],
      },
      {
        heading: "IPPV settings",
        type: "table",
        columns: ["Setting", "Dog", "Cat"],
        rows: [
          ["Tidal volume",       "10–15 mL/kg",  "10–15 mL/kg"],
          ["Peak insp. pressure","10–15 cmH₂O",  "8–12 cmH₂O"],
          ["Resp rate",          "8–12 /min",     "10–15 /min"],
          ["I : E",               "1 : 2",         "1 : 2"],
          ["Target EtCO₂",       "35–45 mmHg",    "35–45 mmHg"],
        ],
      },
      {
        heading: "Hypotension ladder · MAP < 60 mmHg",
        type: "notes",
        items: [
          "1 · Lighten depth — turn vaporiser down 0.5%.",
          "2 · Crystalloid bolus 10 mL/kg IV over 15 min (5 mL/kg in cat).",
          "3 · If med/dex on board, consider atipamezole reverse.",
          "4 · Ephedrine 0.05–0.1 mg/kg IV bolus (may repeat once) OR",
          "     Dobutamine CRI 2–10 µg/kg/min.",
          "5 · Confirm numbers are real — cuff size, ECG leads, waveform.",
        ],
      },
      {
        heading: "Reversal agents",
        type: "table",
        columns: ["Agent", "Reverses", "Dose"],
        rows: [
          ["Atipamezole",     "Med / Dex",           "5× the med µg dose IM"],
          ["Naloxone",        "Opioids",              "0.01–0.04 mg/kg IV"],
          ["Flumazenil",      "Benzodiazepines",      "0.01 mg/kg IV"],
          ["Atropine",        "Vagal brady",          "0.02–0.04 mg/kg IV"],
          ["Glycopyrrolate",  "Vagal brady",          "0.005–0.01 mg/kg IV"],
        ],
      },
      {
        heading: "CPR — BLS + ALS",
        type: "table",
        columns: ["Task", "Target"],
        rows: [
          ["Compression rate", "100–120 /min"],
          ["Compression depth","1/3 to 1/2 chest width"],
          ["Ventilation",      "10 breaths/min · 10 mL/kg"],
          ["Adrenaline (low)", "0.01 mg/kg IV q3–5 min"],
          ["Adrenaline (high)","0.1 mg/kg IV after 10 min no ROSC"],
          ["Atropine",         "0.04 mg/kg IV"],
          ["Defib pVT/VF",     "4–6 J/kg mono · 2–4 J/kg bi"],
        ],
      },
      {
        heading: "Reversible causes — 4H · 4T",
        type: "notes",
        items: [
          "Hypovolaemia · Hypoxia · Hyper/hypokalaemia · Hypothermia",
          "Tension pneumothorax · Tamponade · Toxins · Thrombosis",
        ],
      },
    ],
  },
  {
    /* Table-form successor of `anaesthesia-a3`. Same content, but the four
       volume-vs-weight CHARTS are replaced with tables at 2 kg increments to
       40 kg so nurses can confirm a drawn-up volume by finger-following a
       row rather than reading a graph. If you ever want the charts back,
       flip `hidden: false` on the sibling sheet and hide this one. */
    id: "anaesthesia-a3-tables",
    n: 8,
    title: "Anaesthesia Crib Sheet · A3 (Tables)",
    blurb: "Same anaesthesia crib content as the charts version, but every dose-volume chart is now a look-up table in 2 kg steps to 40 kg — read a weight, read your mL. Ideal for nurses double-checking a drawn-up syringe.",
    accent: "#0f766e",
    Icon: Gauge,
    lastReviewed: "2026-02",
    layout: "a3",
    orientation: "landscape",
    sections: [
      /* Column 1 — Airway & Circuits (unchanged from the charts version) */
      {
        heading: "Endotracheal tube (ETT) sizing",
        type: "table",
        columns: ["Body weight", "Dog · ID (mm)", "Cat · ID (mm)"],
        rows: [
          ["< 2 kg",       "3.0–4.0", "3.0–3.5"],
          ["2–5 kg",       "4.0–5.0", "3.5–4.0"],
          ["5–10 kg",      "5.0–7.0", "4.0–4.5"],
          ["10–20 kg",     "7.0–9.0", "—"],
          ["20–30 kg",     "8.0–10.0", "—"],
          ["30–45 kg",     "9.0–11.0", "—"],
          ["> 45 kg",      "11.0–14.0", "—"],
        ],
        foot: "Brachy: downsize 0.5–1.0 mm. Cats: uncuffed or low-pressure cuff only.",
      },
      {
        heading: "Reservoir (rebreathing) bag",
        type: "table",
        columns: ["Patient weight", "Bag size"],
        rows: [
          ["< 2 kg",    "0.5 L"],
          ["2–7 kg",    "1 L"],
          ["7–15 kg",   "2 L"],
          ["15–40 kg",  "3 L"],
          ["> 40 kg",   "5 L"],
        ],
        foot: "Rule: bag ≥ 3× tidal volume (≈ 30 mL/kg minimum).",
      },
      {
        heading: "Circuit choice & fresh gas flow",
        type: "table",
        columns: ["Circuit / weight", "Induction flow", "Maintenance flow"],
        rows: [
          ["T-piece · < 5 kg",              "300 mL/kg/min",  "Same"],
          ["Bain · 5–10 kg",                "200–300 mL/kg/min", "Same"],
          ["Paed. circle · 10–20 kg",        "30–50 mL/kg/min",  "20–30 mL/kg/min"],
          ["Adult circle · > 20 kg",         "30–50 mL/kg/min",  "20–30 mL/kg/min"],
          ["Low-flow (any circle)",         "—",                "15–20 mL/kg/min"],
        ],
        foot: "Non-rebreathing = no CO₂ absorber → keep flow high. Rebreathing can drop after 10–15 min.",
      },
      {
        heading: "Isoflurane vaporiser dial",
        type: "table",
        columns: ["Phase", "Dog", "Cat"],
        rows: [
          ["Mask / chamber induction",  "3–5% (rare)", "3–4% chamber"],
          ["Maintenance surgical",       "1.5–2.0%",    "1.5–2.0%"],
          ["Short deepening burst",      "up to 2.5%",  "up to 2.5%"],
          ["Recovery (off + O₂)",         "O₂ 5–10 min", "O₂ 5–10 min"],
        ],
        foot: "MAC dog 1.28% · cat 1.63%. Potent opioid CRI → cut dial 20–30%.",
      },
      {
        heading: "Sevoflurane vaporiser dial",
        type: "table",
        columns: ["Phase", "Dog", "Cat"],
        rows: [
          ["Mask / chamber",  "5–7%",     "5–6%"],
          ["Maintenance",     "2.5–3.5%", "2.5–3.5%"],
          ["Recovery",        "O₂ 5 min", "O₂ 5 min"],
        ],
        foot: "MAC dog 2.3% · cat 2.6%.",
      },
      {
        heading: "Local anaesthetic MAX dose",
        type: "table",
        columns: ["Agent", "Dog", "Cat", "Onset · Duration"],
        rows: [
          ["Lidocaine 2%",     "10 mg/kg", "4 mg/kg", "3 min · 60–90 min"],
          ["Bupivacaine 0.5%", "2 mg/kg",  "1 mg/kg", "15 min · 4–6 hr"],
          ["Ropivacaine 0.75%","2 mg/kg",  "1 mg/kg", "10 min · 4–6 hr"],
          ["Mepivacaine 2%",   "5 mg/kg",  "3 mg/kg", "5 min · 90 min–3 hr"],
        ],
        foot: "Aspirate before injection. Combined blocks: sum toward the same limit.",
      },

      /* Column 2 — Volume LOOK-UP TABLES (replace the 4 charts) + protocols */
      {
        heading: "Propofol · volume to draw (IV induction · 10 mg/mL vial)",
        type: "table",
        columns: ["Weight (kg)", "Low · 4 mg/kg (mL)", "High · 6 mg/kg (mL)"],
        rows: [
          ["2",  "0.80",  "1.20"],
          ["4",  "1.60",  "2.40"],
          ["6",  "2.40",  "3.60"],
          ["8",  "3.20",  "4.80"],
          ["10", "4.00",  "6.00"],
          ["12", "4.80",  "7.20"],
          ["14", "5.60",  "8.40"],
          ["16", "6.40",  "9.60"],
          ["18", "7.20",  "10.80"],
          ["20", "8.00",  "12.00"],
          ["22", "8.80",  "13.20"],
          ["24", "9.60",  "14.40"],
          ["26", "10.40", "15.60"],
          ["28", "11.20", "16.80"],
          ["30", "12.00", "18.00"],
          ["32", "12.80", "19.20"],
          ["34", "13.60", "20.40"],
          ["36", "14.40", "21.60"],
          ["38", "15.20", "22.80"],
          ["40", "16.00", "24.00"],
        ],
        foot: "Dog & cat both dosed 4–6 mg/kg IV. Titrate ¼ at a time. Worked example: 20 kg dog at 4 mg/kg = 80 mg ÷ 10 = 8 mL.",
      },
      {
        heading: "Alfaxalone · volume to draw (IV induction · 10 mg/mL vial)",
        type: "table",
        columns: ["Weight (kg)", "Dog low · 2 mg/kg", "Dog high · 3 mg/kg", "Cat low · 2 mg/kg", "Cat high · 5 mg/kg"],
        rows: [
          ["2",  "0.40", "0.60", "0.40", "1.00"],
          ["4",  "0.80", "1.20", "0.80", "2.00"],
          ["6",  "1.20", "1.80", "1.20", "3.00"],
          ["8",  "1.60", "2.40", "1.60", "4.00"],
          ["10", "2.00", "3.00", "2.00", "5.00"],
          ["12", "2.40", "3.60", "2.40", "6.00"],
          ["14", "2.80", "4.20", "2.80", "7.00"],
          ["16", "3.20", "4.80", "3.20", "8.00"],
          ["18", "3.60", "5.40", "3.60", "9.00"],
          ["20", "4.00", "6.00", "4.00", "10.00"],
          ["22", "4.40", "6.60", "4.40", "11.00"],
          ["24", "4.80", "7.20", "4.80", "12.00"],
          ["26", "5.20", "7.80", "5.20", "13.00"],
          ["28", "5.60", "8.40", "5.60", "14.00"],
          ["30", "6.00", "9.00", "6.00", "15.00"],
          ["32", "6.40", "9.60", "6.40", "16.00"],
          ["34", "6.80", "10.20", "6.80", "17.00"],
          ["36", "7.20", "10.80", "7.20", "18.00"],
          ["38", "7.60", "11.40", "7.60", "19.00"],
          ["40", "8.00", "12.00", "8.00", "20.00"],
        ],
        foot: "All mL. Give slowly to avoid transient apnoea. Worked example: 5 kg cat at 3 mg/kg = 15 mg ÷ 10 = 1.5 mL.",
      },
      {
        heading: "Methadone · volume to draw (IM pre-med · 10 mg/mL vial)",
        type: "table",
        columns: ["Weight (kg)", "Dog low · 0.2 mg/kg", "Dog high · 0.3 mg/kg", "Cat low · 0.1 mg/kg", "Cat high · 0.3 mg/kg"],
        rows: [
          ["2",  "0.04", "0.06", "0.02", "0.06"],
          ["4",  "0.08", "0.12", "0.04", "0.12"],
          ["6",  "0.12", "0.18", "0.06", "0.18"],
          ["8",  "0.16", "0.24", "0.08", "0.24"],
          ["10", "0.20", "0.30", "0.10", "0.30"],
          ["12", "0.24", "0.36", "0.12", "0.36"],
          ["14", "0.28", "0.42", "0.14", "0.42"],
          ["16", "0.32", "0.48", "0.16", "0.48"],
          ["18", "0.36", "0.54", "0.18", "0.54"],
          ["20", "0.40", "0.60", "0.20", "0.60"],
          ["22", "0.44", "0.66", "0.22", "0.66"],
          ["24", "0.48", "0.72", "0.24", "0.72"],
          ["26", "0.52", "0.78", "0.26", "0.78"],
          ["28", "0.56", "0.84", "0.28", "0.84"],
          ["30", "0.60", "0.90", "0.30", "0.90"],
          ["32", "0.64", "0.96", "0.32", "0.96"],
          ["34", "0.68", "1.02", "0.34", "1.02"],
          ["36", "0.72", "1.08", "0.36", "1.08"],
          ["38", "0.76", "1.14", "0.38", "1.14"],
          ["40", "0.80", "1.20", "0.40", "1.20"],
        ],
        foot: "All mL. Worked example: 20 kg dog at 0.3 mg/kg = 6 mg ÷ 10 = 0.6 mL.",
      },
      {
        heading: "Dexmedetomidine · volume to draw (IM pre-med · 500 µg/mL vial)",
        type: "table",
        columns: ["Weight (kg)", "Dog low · 3 µg/kg", "Dog high · 10 µg/kg", "Cat low · 5 µg/kg", "Cat high · 20 µg/kg"],
        rows: [
          ["2",  "0.01", "0.04", "0.02", "0.08"],
          ["4",  "0.02", "0.08", "0.04", "0.16"],
          ["6",  "0.04", "0.12", "0.06", "0.24"],
          ["8",  "0.05", "0.16", "0.08", "0.32"],
          ["10", "0.06", "0.20", "0.10", "0.40"],
          ["12", "0.07", "0.24", "0.12", "0.48"],
          ["14", "0.08", "0.28", "0.14", "0.56"],
          ["16", "0.10", "0.32", "0.16", "0.64"],
          ["18", "0.11", "0.36", "0.18", "0.72"],
          ["20", "0.12", "0.40", "0.20", "0.80"],
          ["22", "0.13", "0.44", "0.22", "0.88"],
          ["24", "0.14", "0.48", "0.24", "0.96"],
          ["26", "0.16", "0.52", "0.26", "1.04"],
          ["28", "0.17", "0.56", "0.28", "1.12"],
          ["30", "0.18", "0.60", "0.30", "1.20"],
          ["32", "0.19", "0.64", "0.32", "1.28"],
          ["34", "0.20", "0.68", "0.34", "1.36"],
          ["36", "0.22", "0.72", "0.36", "1.44"],
          ["38", "0.23", "0.76", "0.38", "1.52"],
          ["40", "0.24", "0.80", "0.40", "1.60"],
        ],
        foot: "All mL. Reverse: atipamezole IM = 5× the dex µg dose. Worked example: 20 kg dog at 5 µg/kg = 100 µg ÷ 500 = 0.2 mL.",
      },
      {
        heading: "Pre-med combos · Dog (IM)",
        type: "table",
        columns: ["Patient", "Combination"],
        rows: [
          ["Healthy adult", "Medetomidine 5–10 + Methadone 0.2–0.3"],
          ["Nervous",       "Dex 5 + Butorphanol 0.2"],
          ["Painful",       "Medetomidine 10 + Methadone 0.3"],
          ["Frail",         "Butorphanol 0.2 ± Ace 0.01"],
        ],
        foot: "All doses µg or mg /kg IM, 15–20 min before induction.",
      },
      {
        heading: "Pre-med combos · Cat (IM)",
        type: "table",
        columns: ["Patient", "Combination"],
        rows: [
          ["Healthy adult", "Dex 5 + Buprenorphine 0.02"],
          ["Painful",       "Dex 10 + Methadone 0.3"],
          ["Fractious",     "Dex 10 + Ketamine 5 + Buprenorphine 0.02"],
          ["Frail",         "Butorphanol 0.2 alone"],
        ],
      },
      {
        heading: "Intra-op analgesic CRIs",
        type: "table",
        columns: ["Drug", "Loading", "CRI rate", "Notes"],
        rows: [
          ["Fentanyl",           "2–5 µg/kg IV",  "2–10 µg/kg/hr", "MAC ↓ 20–30%"],
          ["Ketamine",           "0.5 mg/kg IV",  "0.6 mg/kg/hr",  "Wind-up prevention"],
          ["Lidocaine (dog)",    "1–2 mg/kg IV",  "25–50 µg/kg/min","CATS: DO NOT USE"],
          ["MLK (dog)",          "See loading",   "Combined bag",   "Multi-modal std."],
        ],
      },

      /* Column 3 — Monitoring, IPPV, Emergency (unchanged from the charts version) */
      {
        heading: "Intra-op monitoring targets",
        type: "table",
        columns: ["Parameter", "Dog", "Cat", "Alert"],
        rows: [
          ["HR",         "60–140",     "120–200",    "Out of range → investigate"],
          ["SBP",        "> 90 mmHg",   "> 90 mmHg",   "Hypotensive"],
          ["MAP",        "> 60 mmHg",   "> 60 mmHg",   "Intervene"],
          ["SpO₂",       "≥ 95%",        "≥ 95%",        "< 92% → check circuit + tube"],
          ["EtCO₂",      "35–45",        "35–45",        "> 55 → ventilate · < 25 → deep"],
          ["Temp (°C)",  "36.5–38.5",    "36.5–38.5",    "< 36 → warm; < 34 = severe"],
        ],
      },
      {
        heading: "Plane of anaesthesia (Stage III)",
        type: "table",
        columns: ["Plane", "Eye", "Palpebral", "Jaw", "Vitals"],
        rows: [
          ["Light",     "Central",       "Present",     "Firm",     "May move"],
          ["Surgical",  "Ventromedial",   "Slow / abs",  "Slight",   "Stable"],
          ["Deep",      "Central",       "Absent",      "Flaccid",  "BP falls"],
          ["Too deep",  "Central + dry", "Absent",      "None",     "LIGHTEN — arrest imminent"],
        ],
      },
      {
        heading: "IPPV settings",
        type: "table",
        columns: ["Setting", "Dog", "Cat"],
        rows: [
          ["Tidal volume",       "10–15 mL/kg",  "10–15 mL/kg"],
          ["Peak insp. pressure","10–15 cmH₂O",  "8–12 cmH₂O"],
          ["Resp rate",          "8–12 /min",     "10–15 /min"],
          ["I : E",               "1 : 2",         "1 : 2"],
          ["Target EtCO₂",       "35–45 mmHg",    "35–45 mmHg"],
        ],
      },
      {
        heading: "Hypotension ladder · MAP < 60 mmHg",
        type: "notes",
        items: [
          "1 · Lighten depth — turn vaporiser down 0.5%.",
          "2 · Crystalloid bolus 10 mL/kg IV over 15 min (5 mL/kg in cat).",
          "3 · If med/dex on board, consider atipamezole reverse.",
          "4 · Ephedrine 0.05–0.1 mg/kg IV bolus (may repeat once) OR",
          "     Dobutamine CRI 2–10 µg/kg/min.",
          "5 · Confirm numbers are real — cuff size, ECG leads, waveform.",
        ],
      },
      {
        heading: "Reversal agents",
        type: "table",
        columns: ["Agent", "Reverses", "Dose"],
        rows: [
          ["Atipamezole",     "Med / Dex",           "5× the med µg dose IM"],
          ["Naloxone",        "Opioids",              "0.01–0.04 mg/kg IV"],
          ["Flumazenil",      "Benzodiazepines",      "0.01 mg/kg IV"],
          ["Atropine",        "Vagal brady",          "0.02–0.04 mg/kg IV"],
          ["Glycopyrrolate",  "Vagal brady",          "0.005–0.01 mg/kg IV"],
        ],
      },
      {
        heading: "CPR — BLS + ALS",
        type: "table",
        columns: ["Task", "Target"],
        rows: [
          ["Compression rate", "100–120 /min"],
          ["Compression depth","1/3 to 1/2 chest width"],
          ["Ventilation",      "10 breaths/min · 10 mL/kg"],
          ["Adrenaline (low)", "0.01 mg/kg IV q3–5 min"],
          ["Adrenaline (high)","0.1 mg/kg IV after 10 min no ROSC"],
          ["Atropine",         "0.04 mg/kg IV"],
          ["Defib pVT/VF",     "4–6 J/kg mono · 2–4 J/kg bi"],
        ],
      },
      {
        heading: "Reversible causes — 4H · 4T",
        type: "notes",
        items: [
          "Hypovolaemia · Hypoxia · Hyper/hypokalaemia · Hypothermia",
          "Tension pneumothorax · Tamponade · Toxins · Thrombosis",
        ],
      },
    ],
  },

  {
    id: "ward-recovery-a3",
    n: 9,
    title: "Ward & Recovery Crib Sheet · A3",
    blurb: "Everything after the anaesthetic table — post-op recovery checklist, analgesia, fluid balance, nutrition, blood draws, restraint, wound care and discharge instructions.",
    accent: "#b45309",
    Icon: Gauge,
    lastReviewed: "2026-02",
    layout: "a3",
    orientation: "landscape",
    sections: [
      /* Column 1 — Immediate recovery */
      {
        heading: "Immediate recovery checklist (first 30 min)",
        type: "notes",
        items: [
          "Position lateral, head-down 5°, extubate ONLY when swallow reflex returns (or earlier in brachy).",
          "Continue O₂ 5 min via mask/flow-by until stable SpO₂ ≥ 95% on room air.",
          "Take vitals at 5, 15, 30 min — HR, RR, MMs, CRT, temp, pain score.",
          "Active warming until rectal temp ≥ 37 °C. Never leave a shivering patient.",
          "Ensure IV catheter patent — flush + check for extravasation.",
          "Check surgical site: bleeding, swelling, dehiscence — mark first observation time.",
          "Empty bladder before returning to cage if 30+ min under GA.",
          "Record ALL times: extubation, return of sternal, first stand, first eat.",
        ],
      },
      {
        heading: "Recovery red flags — escalate to vet",
        type: "callout",
        tone: "warning",
        body: "Delayed extubation > 30 min · SpO₂ < 92% on room air · HR persistently > 200 (dog) or > 220 (cat) · MAP < 60 unresponsive · Regurgitation or aspiration · Uncontrolled dysphoria · Rectal temp < 36 or > 40 °C at 30 min · Any bleeding through the dressing · No pedal reflex at 45 min.",
      },
      {
        heading: "Post-op analgesia · Dog",
        type: "table",
        columns: ["Drug", "Dose", "Route", "Frequency"],
        rows: [
          ["Methadone",       "0.1–0.3 mg/kg",  "IM / IV",    "q4–6h"],
          ["Buprenorphine",   "0.01–0.02 mg/kg","IM/IV/OTM",  "q6–8h"],
          ["Meloxicam",       "0.1 mg/kg",       "PO / SC",    "q24h (up to 4 days)"],
          ["Carprofen",       "2 mg/kg",         "PO / SC",    "q12h"],
          ["Gabapentin",      "10 mg/kg",        "PO",         "q8h (adjunct)"],
        ],
        foot: "Multi-modal always: opioid + NSAID + local block whenever safe.",
      },
      {
        heading: "Post-op analgesia · Cat",
        type: "table",
        columns: ["Drug", "Dose", "Route", "Frequency"],
        rows: [
          ["Buprenorphine",   "0.02–0.04 mg/kg","IM/IV/OTM",  "q6–8h"],
          ["Methadone",       "0.1–0.2 mg/kg",  "IM / IV",    "q4–6h"],
          ["Meloxicam",       "0.05 mg/kg",     "SC / PO",    "q24h SHORT course"],
          ["Robenacoxib",     "1–2 mg/kg",      "SC / PO",    "q24h × up to 6 days"],
          ["Gabapentin",      "5–10 mg/kg",     "PO",         "q8–12h"],
        ],
        foot: "Cats + NSAIDs: hydration and renal function normal before dosing.",
      },

      /* Column 2 — Hospitalization workflow */
      {
        heading: "Fluid balance in-patient (per 24 h)",
        type: "table",
        columns: ["Component", "Formula"],
        rows: [
          ["Maintenance",      "Dog 2 mL/kg/hr · Cat 1.5 mL/kg/hr"],
          ["Dehydration deficit","BW × % dehydration × 10 (mL); replace 12–24 h"],
          ["Ongoing losses",   "Estimate vom/diarr/drain/urine mL for mL"],
          ["Insensible",       "≈ 20 mL/kg/day (baked into maintenance)"],
        ],
        foot: "Total = maintenance + deficit + ongoing. Halve maintenance in cardiac / renal.",
      },
      {
        heading: "Nutrition — RER & feeding",
        type: "table",
        columns: ["Item", "Formula / value"],
        rows: [
          ["RER (kcal/day)",             "70 × BW(kg)^0.75  · shortcut 30 × BW + 70 (2–45 kg)"],
          ["Post-GA feeding",              "Small offering as soon as swallow returns + no nausea"],
          ["NG / oesoph tube start",      "Day 1: RER/4 · Day 2: RER/2 · Day 3: full RER"],
          ["Water free access",            "As soon as extubated + swallowing"],
        ],
        foot: "Not eating by day 3 → tube feed. Anorexia has its own morbidity.",
      },
      {
        heading: "IV catheter & bandage care",
        type: "notes",
        items: [
          "Check q4h: patency (flush), swelling, warmth, wetness, patient interference.",
          "Reset catheter site q72h maximum, sooner if any concern.",
          "Bandage: check toes q4h for swelling, temp, colour — capillary refill in the paw.",
          "Chart every check with initials — a missed check is a bandage disaster.",
          "Any wet, smelly or slipped bandage → remove and reassess immediately.",
        ],
      },
      {
        heading: "Blood draw sites & max volumes",
        type: "table",
        columns: ["Site", "Species", "Max single draw"],
        rows: [
          ["Jugular",          "Dog & cat",  "Up to 10% blood volume (dog 90 mL/kg · cat 60 mL/kg × 0.10)"],
          ["Cephalic",         "Dog",        "Up to 5 mL typical clinical draw"],
          ["Medial saphenous", "Cat",         "Up to 2 mL typical clinical draw"],
          ["Lateral saphenous","Dog",         "Up to 5 mL"],
        ],
        foot: "Total blood volume: dog ≈ 90 mL/kg · cat ≈ 60 mL/kg.",
      },
      {
        heading: "Restraint by species",
        type: "notes",
        items: [
          "Dog jugular: sit on rump, arm under chin, other arm around chest, muzzle raised.",
          "Cat jugular: burrito wrap in towel, chin raised, hindlimbs contained — never scruff.",
          "Rabbit: bunny burrito, support hindlimbs; never scruff or lift by ears.",
          "Cat cephalic: kitty kong or towel wrap, elbow extended, distraction with churu.",
          "Dog cephalic: elbow extended, thumb rolling vein lateral, minimum restraint that works.",
          "Fractious patient: STOP, pre-medicate rather than escalate physical restraint.",
        ],
      },

      /* Column 3 — Emergency triage + discharge */
      {
        heading: "Emergency triage — ABCDE",
        type: "table",
        columns: ["Letter", "Assess", "Do"],
        rows: [
          ["A · Airway",   "Patent? Snoring? Cyanotic?",    "Extend neck, suction, intubate"],
          ["B · Breathing","Rate, effort, symmetry, SpO₂",  "O₂ mask/cage, thoracocentesis if PTX"],
          ["C · Circulation","HR, MMs, CRT, pulse, BP",     "IV access, fluids to effect, stop bleeding"],
          ["D · Disability","Mentation, pupils, pain",       "Head trauma, hypoglycaemia, toxin?"],
          ["E · Exposure", "Full body, temp, wounds",        "Warm, wound-check, chart everything"],
        ],
      },
      {
        heading: "History — SAMPLE",
        type: "notes",
        items: [
          "S · Signs — chief complaint, onset, progression.",
          "A · Allergies to drugs, food, environmental.",
          "M · Medications — everything on board incl. supplements.",
          "P · Past medical / surgical / anaesthetic history.",
          "L · Last meal and last water (fasting critical).",
          "E · Events leading to presentation — trauma, exposure, seizure.",
        ],
      },
      {
        heading: "Common ward medications",
        type: "table",
        columns: ["Drug", "Dose", "Route"],
        rows: [
          ["Maropitant",   "1 mg/kg q24h",                              "SC/IV/PO"],
          ["Metoclopramide","0.5–1 mg/kg q6h OR 1–2 mg/kg/day CRI",     "IV/SC"],
          ["Ondansetron",  "0.5–1 mg/kg q8–12h",                        "IV/SC/PO"],
          ["Ranitidine",   "2 mg/kg q12h",                              "IV/PO"],
          ["Omeprazole",   "1 mg/kg q12h",                              "PO"],
          ["Pantoprazole", "1 mg/kg q24h",                              "IV"],
        ],
      },
      {
        heading: "Wound & dressing types",
        type: "table",
        columns: ["Stage", "Dressing", "Change"],
        rows: [
          ["Debridement (dirty)",     "Wet-to-dry hypertonic",         "12–24 h"],
          ["Debridement (necrotic)",  "Honey / hydrogel",              "24–48 h"],
          ["Granulating",             "Non-adherent (Melolin, Adaptic)","48–72 h"],
          ["Epithelialising",         "Occlusive (Opsite, film)",       "3–5 days"],
          ["Post-op clean",           "Light non-adherent + bandage",  "24 h then air"],
        ],
      },
      {
        heading: "Client discharge — verbal + written",
        type: "notes",
        items: [
          "Home for 24 h quiet; no strenuous exercise for 7–10 days.",
          "Diet: small usual food tonight; back to normal tomorrow if no vomiting.",
          "Medications: name, dose, timing, side effects, when to call.",
          "Wound: check twice daily for redness, swelling, discharge, dehiscence.",
          "Elizabethan collar / body suit ON at all times for 10–14 days.",
          "Suture removal (if any) at 10–14 days — book the appointment now.",
          "Call the clinic if: not eating by 24 h · vomiting > once · bleeding · pain not managed.",
          "Emergency after-hours number written on the discharge sheet and confirmed.",
        ],
      },
    ],
  },

];

export const CHEAT_SHEET_BY_ID = Object.fromEntries(
  CHEAT_SHEETS.map((s) => [s.id, s]),
);
